import React from "react";
import type { BaseEntity, SimpleTableConfig } from "./types";
import { useSimpleTableHooks } from "./hooks/useSimpleTableHooks";
import { useSimpleTableState } from "./hooks/useSimpleTableState";
import { useSimpleTableData } from "./hooks/useSimpleTableData";
import { useFilterContext } from "@/contexts/FilterContext";

/**
 * Custom hook that initializes all hooks and performs initial data processing.
 * This hook must be called unconditionally at the top of the component.
 *
 * CRITICAL: Hook ordering is preserved here to avoid "rendered fewer hooks than expected" errors.
 */
export function useSimpleTableSetup<TData extends BaseEntity>({
  config,
  data,
  propCurrentPage,
  propOnPageChange,
  propPageSize,
  propOnPageSizeChange,
  isDataPreFiltered,
  propTotalItems,
  disableClientSlice,
}: {
  config: SimpleTableConfig<TData>;
  data: TData[];
  propCurrentPage?: number;
  propOnPageChange?: (page: number) => void;
  propPageSize?: number;
  propOnPageSizeChange?: (size: number) => void;
  isDataPreFiltered?: boolean;
  propTotalItems?: number;
  disableClientSlice?: boolean;
}) {
  // ============================================================================
  // CRITICAL: ALL HOOKS MUST BE CALLED HERE - BEFORE ANY CONDITIONAL LOGIC
  // This fixes the "rendered fewer hooks than expected" error
  // ============================================================================

  // Call all hooks unconditionally at the top
  const tableHooks = useSimpleTableHooks();
  const filterContext = useFilterContext<TData>();

  const {
    isMobile,
    hasMounted,
    appUser,
    filterFieldsByRole,
    roleName,
    contextVisibleColumns,
    contextToggleColumn,
    contextColumnOrder,
    contextSetColumnOrder,
    tablePrefsKey,
    openModal,
    containerRef,
    tableHeaderScrollRef,
    tableBodyRef,
  } = tableHooks;

  // ============================================================================
  // STATE MANAGEMENT - Using extracted hook
  // ============================================================================

  const state = useSimpleTableState({
    config,
    propCurrentPage,
    propOnPageChange,
    propPageSize,
    propOnPageSizeChange,
    contextColumnOrder,
    contextSetColumnOrder,
    tablePrefsKey,
  });

  // Convert context columns to Set for compatibility
  const visibleColumns = React.useMemo(() => {
    const ids = contextVisibleColumns
      .map((col) => col.id)
      .filter((id) => !config.fields[id]?.formOnly);
    return new Set(ids);
  }, [contextVisibleColumns, config.fields]);

  // Extract quickFilterState early for use in callbacks
  const { quickFilterState, setQuickFilterState } = state;

  // ============================================================================
  // DATA PROCESSING - Using extracted hook
  // ============================================================================

  const { visibleFields, processedData, paginatedData, totalPages } = useSimpleTableData({
    config,
    data,
    state,
    callbacks: {} as any, // Will be populated later
    hooks: tableHooks,
    isDataPreFiltered: isDataPreFiltered ?? false,
    propTotalItems,
    visibleColumns,
    disableClientSlice,
  });

  return {
    tableHooks,
    filterContext,
    isMobile,
    hasMounted,
    appUser,
    filterFieldsByRole,
    roleName,
    contextVisibleColumns,
    contextToggleColumn,
    contextColumnOrder,
    contextSetColumnOrder,
    openModal,
    containerRef,
    tableHeaderScrollRef,
    tableBodyRef,
    state,
    visibleColumns,
    quickFilterState,
    setQuickFilterState,
    visibleFields,
    processedData,
    paginatedData,
    totalPages,
    config,
    data,
  };
}

export type SimpleTableSetupResult<TData extends BaseEntity = BaseEntity> = ReturnType<
  typeof useSimpleTableSetup<TData>
>;
